import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { motion, AnimatePresence } from 'framer-motion';
import { FormProgress } from './FormComponents';
import GettingStarted from './LoanApplicationSections/GettingStarted';
import PersonalInfo from './LoanApplicationSections/PersonalInfo';
import ContactInfo from './LoanApplicationSections/ContactInfo';
import Employment from './LoanApplicationSections/Employment';
import Income from './LoanApplicationSections/Income';
import Assets from './LoanApplicationSections/Assets';
import Liabilities from './LoanApplicationSections/Liabilities';
import ReviewSubmit from './LoanApplicationSections/ReviewSubmit';
import { fetchWithAuth, BASE_URL } from '../../lender/api/client';

const STORAGE_KEY = 'loan_application_draft';

const SECTION_NAMES = [
  'Getting Started',
  'Personal Info',
  'Contact',
  'Employment',
  'Income',
  'Assets',
  'Liabilities',
  'Review',
];

const initialFormData = {
  gettingStarted: {
    loanPurpose: '',
    propertyType: '',
    propertyUse: '',
  },
  personalInfo: {
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    dateOfBirth: '',
    ssn: '',
    citizenship: '',
    maritalStatus: '',
  },
  contactInfo: {
    streetAddress: '',
    unit: '',
    city: '',
    state: '',
    zipCode: '',
    yearsAtAddress: '',
    prevStreetAddress: '',
    prevCity: '',
    prevState: '',
    prevZipCode: '',
    phone: '',
    email: '',
    agreeToContact: false,
  },
  employment: {
    employmentStatus: '',
    employerName: '',
    jobTitle: '',
    employerAddress: '',
    employerPhone: '',
    startDate: '',
    yearsAtJob: '',
    monthlyIncome: '',
    prevEmployerName: '',
    prevJobTitle: '',
    prevStartDate: '',
    prevEndDate: '',
    employmentDescription: '',
  },
  income: {
    incomes: [],
  },
  assets: {
    assets: [],
  },
  liabilities: {
    liabilities: [],
  },
};

export default function LoanApplicationForm({ caseInfo, onComplete, onError }) {
  const [currentSection, setCurrentSection] = useState(0);
  const [formData, setFormData] = useState(initialFormData);
  const [isLoading, setIsLoading] = useState(true);

  // Load saved data on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Merge with initial data to ensure all fields exist
        setFormData({
          gettingStarted: { ...initialFormData.gettingStarted, ...parsed.gettingStarted },
          personalInfo: { ...initialFormData.personalInfo, ...parsed.personalInfo },
          contactInfo: { ...initialFormData.contactInfo, ...parsed.contactInfo },
          employment: { ...initialFormData.employment, ...parsed.employment },
          income: { ...initialFormData.income, ...parsed.income },
          assets: { ...initialFormData.assets, ...parsed.assets },
          liabilities: { ...initialFormData.liabilities, ...parsed.liabilities },
        });
      }

      // Pre-fill email from case info if available
      if (caseInfo?.email || caseInfo?.borrowerEmail) {
        setFormData(prev => ({
          ...prev,
          contactInfo: {
            ...prev.contactInfo,
            email: prev.contactInfo.email || caseInfo.email || caseInfo.borrowerEmail,
          }
        }));
      }
    } catch (e) {
      console.error('Error loading saved form data:', e);
    }
    setIsLoading(false);
  }, [caseInfo]);

  // Auto-save on data change
  useEffect(() => {
    if (!isLoading) {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(formData));
      } catch (e) {
        console.error('Error saving form data:', e);
      }
    }
  }, [formData, isLoading]);

  const updateSection = useCallback((section, data) => {
    setFormData(prev => ({
      ...prev,
      [section]: data,
    }));
  }, []);

  const goNext = useCallback(() => {
    setCurrentSection(prev => Math.min(prev + 1, SECTION_NAMES.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goBack = useCallback(() => {
    setCurrentSection(prev => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const goToSection = useCallback((index) => {
    setCurrentSection(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const handleSubmit = async () => {
    // Prepare the submission payload matching API spec
    const payload = {
      caseId: caseInfo?.id || caseInfo?.caseId,
      caseNumber: caseInfo?.caseNumber,
      borrowerEmail: formData.contactInfo?.email || caseInfo?.email || caseInfo?.borrowerEmail,
      borrowerName: `${formData.personalInfo?.firstName || ''} ${formData.personalInfo?.lastName || ''}`.trim(),
      application: {
        gettingStarted: formData.gettingStarted,
        personalInfo: {
          firstName: formData.personalInfo?.firstName || '',
          middleName: formData.personalInfo?.middleName || '',
          lastName: formData.personalInfo?.lastName || '',
          suffix: formData.personalInfo?.suffix || '',
          dateOfBirth: formData.personalInfo?.dateOfBirth || '',
          ssnLast4: formData.personalInfo?.ssn?.slice(-4) || '',
          citizenship: formData.personalInfo?.citizenship || '',
          maritalStatus: formData.personalInfo?.maritalStatus || '',
        },
        contactInfo: formData.contactInfo,
        employment: formData.employment,
        income: formData.income,
        assets: formData.assets,
        liabilities: formData.liabilities,
      },
      submittedAt: new Date().toISOString(),
    };

    try {
      const response = await fetchWithAuth(`${BASE_URL}/api/loan-applications`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to submit application');
      }

      const result = await response.json().catch(() => ({}));

      // Clear the draft on successful submission
      localStorage.removeItem(STORAGE_KEY);

      // Notify parent component with API response
      onComplete({ ...payload, ...result });
    } catch (error) {
      console.error('Error submitting application:', error);
      onError?.(error.message || 'Unable to submit application. Please try again.');
      throw error; // Re-throw so ReviewSubmit can handle loading state
    }
  };

  if (isLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>
        Loading...
      </div>
    );
  }

  const renderSection = () => {
    const props = {
      onNext: goNext,
      onBack: goBack,
    };

    switch (currentSection) {
      case 0:
        return (
          <GettingStarted
            data={formData.gettingStarted}
            onChange={(data) => updateSection('gettingStarted', data)}
            {...props}
          />
        );
      case 1:
        return (
          <PersonalInfo
            data={formData.personalInfo}
            onChange={(data) => updateSection('personalInfo', data)}
            {...props}
          />
        );
      case 2:
        return (
          <ContactInfo
            data={formData.contactInfo}
            onChange={(data) => updateSection('contactInfo', data)}
            {...props}
          />
        );
      case 3:
        return (
          <Employment
            data={formData.employment}
            onChange={(data) => updateSection('employment', data)}
            {...props}
          />
        );
      case 4:
        return (
          <Income
            data={formData.income}
            onChange={(data) => updateSection('income', data)}
            {...props}
          />
        );
      case 5:
        return (
          <Assets
            data={formData.assets}
            onChange={(data) => updateSection('assets', data)}
            {...props}
          />
        );
      case 6:
        return (
          <Liabilities
            data={formData.liabilities}
            onChange={(data) => updateSection('liabilities', data)}
            {...props}
          />
        );
      case 7:
        return (
          <ReviewSubmit
            data={formData}
            onBack={goBack}
            onSubmit={handleSubmit}
            onEditSection={goToSection}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div>
      <FormProgress
        currentSection={currentSection}
        totalSections={SECTION_NAMES.length}
        sectionNames={SECTION_NAMES}
      />

      <AnimatePresence mode="wait">
        <motion.div
          key={currentSection}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {renderSection()}
        </motion.div>
      </AnimatePresence>

      {/* Auto-save indicator */}
      <div
        style={{
          marginTop: '24px',
          textAlign: 'center',
          fontSize: '12px',
          color: '#64748b',
        }}
      >
        Your progress is automatically saved
      </div>
    </div>
  );
}

LoanApplicationForm.propTypes = {
  caseInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    caseId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    caseNumber: PropTypes.string,
    email: PropTypes.string,
    borrowerEmail: PropTypes.string,
  }),
  onComplete: PropTypes.func.isRequired,
  onError: PropTypes.func,
};
